import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Table } from 'antd';
import type { TableColumnsType, ThemeConfig } from 'antd';
import {
  useGetOrganizationsQuery,
  useDeleteOrganizationMutation,
  type Organization,
} from '../../features/organizations/organizationsApi';
import { fontFamily } from '../../theme/tokens';
import Pagination from '../../components/Pagination';
import DeleteOrganizationModal from './DeleteOrganizationModal';
import { PlusIcon, EyeIcon, EditSquareIcon, TrashIcon, OrgBuildingIcon } from '../../assets/icons';

const ACTION_BTN =
  'flex items-center justify-center rounded-lg p-2 text-ink-secondary transition-colors hover:bg-brand-shade hover:text-brand';

/**
 * antd Table theme, mapped to the Chunaav / Figma design tokens. Text-transform,
 * tracking and weight for the header aren't theme tokens — those live in the
 * `.chunaav-table` overrides in index.css.
 */
const TABLE_THEME: ThemeConfig = {
  token: { colorBorderSecondary: 'rgba(195,198,214,0.2)', fontFamily },
  components: {
    Table: {
      headerBg: 'rgba(209,226,255,0.5)', // #d1e2ff80
      headerColor: '#46556F', // ink-secondary
      headerSplitColor: 'transparent', // drop the header column separators
      borderColor: 'rgba(195,198,214,0.2)', // line/20 row dividers
      cellPaddingBlock: 24, // py-6
      cellPaddingInline: 32, // px-8
      rowHoverBg: '#f7faff', // brand-bg
      headerBorderRadius: 0,
      colorText: '#00173e', // ink
      fontSize: 14,
    },
  },
};

/** Two-letter initials for the contact avatar fallback (no avatar comes from the API). */
function initials(name?: string | null): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '—';
}

function StatusPill({ status }: { status: string }) {
  const active = /^active$/i.test(status);
  return (
    <span
      className={[
        'inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.55px]',
        active ? 'bg-brand-shade text-brand' : 'bg-muted-bg text-ink-secondary',
      ].join(' ')}
    >
      <span
        className={['size-1.5 rounded-full', active ? 'bg-brand' : 'bg-ink-secondary'].join(' ')}
      />
      {active ? 'active' : 'inactive'}
    </span>
  );
}

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { data, isFetching } = useGetOrganizationsQuery({ page, pageSize });
  const [deleteOrganization, { isLoading: isDeleting }] = useDeleteOrganizationMutation();
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const confirmDelete = async () => {
    if (!orgToDelete) return;
    try {
      await deleteOrganization(orgToDelete.id).unwrap();
      setOrgToDelete(null);
    } catch {
      /* keep the modal open so the user can retry */
    }
  };

  const columns: TableColumnsType<Organization> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, org) => (
        <div className="flex items-center gap-6">
          <span className="flex size-10 items-center justify-center rounded-lg bg-brand-bg text-ink">
            <OrgBuildingIcon className="h-5 w-5" />
          </span>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-ink">{org.name}</span>
            {org.short_name && (
              <span className="text-xs text-ink-secondary">{org.short_name}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      render: (_, org) => <StatusPill status={org.status} />,
    },
    {
      title: 'Contact Person',
      key: 'contact',
      width: 240,
      render: (_, org) => (
        <div className="flex items-center gap-4">
          <span className="flex size-8 items-center justify-center rounded-full bg-brand-avatar2 text-[11px] font-semibold text-brand">
            {initials(org.contact_person)}
          </span>
          <span className="text-sm text-ink">{org.contact_person || '—'}</span>
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      align: 'right',
      render: (_, org) => (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            aria-label="View"
            onClick={() => navigate(`/admin/organizations/${org.id}`)}
            className={ACTION_BTN}
          >
            <EyeIcon className="h-[12.5px] w-[18.333px]" />
          </button>
          <button
            type="button"
            aria-label="Edit"
            onClick={() => navigate(`/admin/organizations/${org.id}/edit`)}
            className={ACTION_BTN}
          >
            <EditSquareIcon className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Delete"
            onClick={() => setOrgToDelete(org)}
            className={[ACTION_BTN, 'hover:text-red-500'].join(' ')}
          >
            <TrashIcon className="size-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">
            Organizations
          </h1>
          <p className="max-w-[672px] text-sm leading-6 text-ink-secondary">
            Manage and view all registered entities within the system. Centralize governance
            through unified departmental tracking.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/admin/organizations/new')}
          className="flex h-10 shrink-0 items-center justify-center gap-2.5 self-start rounded-lg bg-brand px-6 text-[13px] font-medium text-white shadow-btn transition-colors hover:bg-brand-dark sm:self-auto"
        >
          <PlusIcon className="size-[16.667px]" />
          Create New
        </button>
      </div>

      {/* Table card */}
      <div className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-card">
        <ConfigProvider theme={TABLE_THEME}>
          <Table<Organization>
            className="chunaav-table"
            columns={columns}
            dataSource={items}
            rowKey="id"
            pagination={false}
            loading={isFetching}
            scroll={{ x: 'max-content' }}
          />
        </ConfigProvider>

        {/* Footer / pagination */}
        <Pagination
          rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
          rangeEnd={Math.min(page * pageSize, total)}
          total={total}
          noun="entries"
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>

      <DeleteOrganizationModal
        open={orgToDelete !== null}
        orgName={orgToDelete?.name}
        loading={isDeleting}
        onClose={() => setOrgToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
